import { jwtDecode } from 'jwt-decode';

const PREFIX = '@STechTk/';
const listeners = new Set();
let account = null;
let refreshing = null;
let generation = 0;
const storage = (method, key, value) => {
  try { return window.localStorage[method](PREFIX + key, value); } catch { return null; }
};
// Remove credentials saved by the previous implementation. Passwords are never restored.
export const removeLegacyCredentials = () => {
  ['login', 'token', 'userData'].forEach(key => storage('removeItem', key));
};
removeLegacyCredentials();
const notify = () => listeners.forEach(listener => listener(account));
export const subscribeSession = listener => { listeners.add(listener); return () => listeners.delete(listener); };
export const currentAccount = () => account;
export const rememberedEmail = () => storage('getItem', 'rememberedEmail') || '';
export const clearSession = () => { generation += 1; account = null; removeLegacyCredentials(); notify(); };

const authEndpoint = () => {
  const configured = process.env.REACT_APP_AUTH_URI;
  if (configured) return configured.replace(/\/$/, '');
  const url = new URL(process.env.REACT_APP_URI || '/graphql', window.location.origin);
  url.pathname = url.pathname.replace(/\/graphql\/?$/, '') + '/api/session';
  url.search = ''; url.hash = '';
  return url.toString().replace(/\/$/, '');
};
const performRequest = async (action, body = {}) => {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), 15000);
  try {
    const response = await fetch(`${authEndpoint()}/${action}`, {
      method: 'POST', credentials: 'include', signal: abort.signal,
      headers: { 'Content-Type': 'application/json', 'X-Session-Request': 'true' }, body: JSON.stringify(body),
    });
    if (!response.ok) {
      const error = new Error('Session request failed');
      error.status = response.status;
      throw error;
    }
    return response.status === 204 ? null : response.json();
  } finally { clearTimeout(timer); }
};
// Serialize cookie rotation across tabs when Web Locks is available.
const request = (action, body) => navigator.locks?.request
  ? navigator.locks.request('thermoking-session', () => performRequest(action, body))
  : performRequest(action, body);
window.addEventListener('storage', event => {
  if (event.key === PREFIX + 'loggedOut' && event.newValue === 'true') clearSession();
});
const acceptAccount = value => {
  if (!value || typeof value.accessToken !== 'string' || !value.accessToken) throw new Error('Invalid session response');
  const decoded = jwtDecode(value.accessToken);
  if (!decoded.id || !Number.isFinite(decoded.exp) || decoded.exp * 1000 <= Date.now()) throw new Error('Invalid session token');
  account = { id: Number(value.id || decoded.id), name: String(value.name || ''), email: String(value.email || ''), customer: value.customer, accessToken: value.accessToken };
  notify();
  return account;
};
export const loginSession = async ({ email, password, remember }) => {
  const version = ++generation;
  const value = await request('login', { email: email.trim(), password, remember: !!remember });
  if (version !== generation) throw new Error('Session replaced');
  storage('removeItem', 'loggedOut');
  const result = acceptAccount(value);
  if (remember) storage('setItem', 'rememberedEmail', email.trim());
  else storage('removeItem', 'rememberedEmail');
  return result;
};
export const refreshSession = () => {
  if (storage('getItem', 'loggedOut') === 'true') {
    const error = new Error('Logged out'); error.status = 401; return Promise.reject(error);
  }
  if (refreshing) return refreshing;
  const version = generation;
  refreshing = request('refresh').then(value => {
    if (version !== generation) throw new Error('Session replaced');
    return acceptAccount(value);
  }).catch(error => {
    if (version === generation && error.status === 401) clearSession();
    throw error;
  }).finally(() => { refreshing = null; });
  return refreshing;
};
export const accessToken = async () => {
  if (!account) return '';
  const { exp } = jwtDecode(account.accessToken);
  if (exp * 1000 - Date.now() < 30000) await refreshSession();
  return account?.accessToken || '';
};
export const logoutSession = async () => {
  storage('setItem', 'loggedOut', 'true');
  clearSession();
  await request('logout');
};
